import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../components/AppIcon';
import CorporateSelect from '../../components/ui/CorporateSelect';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart } from 'recharts';
import { tiendaDb } from '../../lib/supabase';
import {  } from '../../services/tiendaService';

const ACCENT = '#34D399';
const GOLD = '#D6EF4E';
const BLUE = '#60A5FA';
const PURPLE = '#A78BFA';
const ORANGE = '#FB923C';
const RED = '#F87171';
const AMBER = '#FBBF24';

const fmt = (n, d = 2) => typeof n === 'number' ? n?.toLocaleString('es-ES', { minimumFractionDigits: d, maximumFractionDigits: d }) : '—';
const fmtEur = (n) => typeof n === 'number' ? `${fmt(n)} €` : '—';
const fmtPct = (n) => typeof n === 'number' ? `${fmt(n, 1)}%` : '—';

const DATE_FILTERS = ['Hoy', 'Ayer', 'Semana', 'Mes', 'Trimestre', 'Año'];
const CHANNEL_FILTERS = ['Consolidado', 'Físico', 'Online'];
const COLORS_CHART = [ACCENT, GOLD, BLUE, PURPLE, ORANGE, RED];

const getDateRange = (filter) => {
  const now = new Date();
  const today = now?.toISOString()?.split('T')?.[0];
  switch (filter) {
    case 'Hoy': return { from: today, to: today };
    case 'Ayer': {
      const y = new Date(now); y?.setDate(y?.getDate() - 1);
      const yd = y?.toISOString()?.split('T')?.[0];
      return { from: yd, to: yd };
    }
    case 'Semana': {
      const w = new Date(now); w?.setDate(w?.getDate() - 7);
      return { from: w?.toISOString()?.split('T')?.[0], to: today };
    }
    case 'Mes': {
      const m = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: m?.toISOString()?.split('T')?.[0], to: today };
    }
    case 'Trimestre': {
      const q = new Date(now); q?.setMonth(q?.getMonth() - 3);
      return { from: q?.toISOString()?.split('T')?.[0], to: today };
    }
    case 'Año': {
      const a = new Date(now.getFullYear(), 0, 1);
      return { from: a?.toISOString()?.split('T')?.[0], to: today };
    }
    default: return { from: null, to: null };
  }
};

const KPICard = ({ label, value, sub, icon, color, loading }) => (
  <div className="rounded-2xl p-4 flex flex-col gap-2" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
    <div className="flex items-center justify-between">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}15`, border: `1px solid ${color}30` }}>
        <Icon name={icon} size={15} style={{ color }} />
      </div>
    </div>
    <p className="font-oranienbaum font-bold text-white text-xl">{loading ? '...' : value}</p>
    <div>
      <p className="font-oranienbaum text-white/60 text-xs leading-tight">{label}</p>
      {sub && <p className="font-oranienbaum text-white/25 text-[10px]">{sub}</p>}
    </div>
  </div>
);

const TiendaGeneral = () => {
  const [dateFilter, setDateFilter] = useState('Mes');
  const [channelFilter, setChannelFilter] = useState('Consolidado');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [evolucion, setEvolucion] = useState([]);
  const [familiasData, setFamiliasData] = useState([]);
  const [vencimientos, setVencimientos] = useState([]);
  const [alertasStock, setAlertasStock] = useState({ sinStock: 0, bajoMinimo: 0 });

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const range = getDateRange(dateFilter);

      // Ventas
      const { data: ventas } = await tiendaDb?.from('ventas')?.select('total, base_imponible, canal, estado, fecha')?.gte('fecha', range?.from || '2020-01-01')?.lte('fecha', (range?.to || new Date()?.toISOString()?.split('T')?.[0]) + 'T23:59:59')?.neq('estado', 'ANULADO');

      const ventasFiltradas = (ventas || [])?.filter(v => {
        if (channelFilter === 'Físico') return v?.canal === 'TIENDA';
        if (channelFilter === 'Online') return v?.canal === 'ONLINE';
        return true;
      });

      const totalVentas = ventasFiltradas?.reduce((s, v) => s + (v?.total || 0), 0);
      const totalBase = ventasFiltradas?.reduce((s, v) => s + (v?.base_imponible || 0), 0);
      const numTickets = ventasFiltradas?.length;
      const ticketMedio = numTickets > 0 ? totalVentas / numTickets : 0;

      // Compras del mes
      const now = new Date();
      const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)?.toISOString()?.split('T')?.[0];
      const { data: compras } = await tiendaDb?.from('facturas_compra')?.select('total, estado_pago, pendiente_pago, fecha_vencimiento, fecha_factura')?.neq('estado_pago', 'ANULADA');

      const comprasMes = (compras || [])?.filter(c => c?.fecha_factura >= firstOfMonth)?.reduce((s, c) => s + (c?.total || 0), 0);
      const pendientePago = (compras || [])?.filter(c => c?.estado_pago !== 'PAGADA')?.reduce((s, c) => s + (c?.pendiente_pago || c?.total || 0), 0);
      const today = now?.toISOString()?.split('T')?.[0];
      const facturasVencidas = (compras || [])?.filter(c => c?.estado_pago !== 'PAGADA' && c?.fecha_vencimiento && c?.fecha_vencimiento < today)?.length;

      // Inventario
      const { data: inv } = await tiendaDb?.from('inventario_actual')?.select('stock_actual, stock_disponible, ultimo_coste, producto:productos(activo, stock_minimo, pvp_venta, precio_compra, tipo_iva)');

      const stockVal = (inv || [])?.reduce((s, i) => s + (i?.stock_actual || 0) * (i?.ultimo_coste || i?.producto?.precio_compra || 0), 0);
      const valorPvp = (inv || [])?.reduce((s, i) => s + (i?.stock_actual || 0) * (i?.producto?.pvp_venta || 0), 0);
      const sinStock = (inv || [])?.filter(i => (i?.stock_disponible || 0) <= 0 && i?.producto?.activo)?.length;
      const bajoMin = (inv || [])?.filter(i => (i?.stock_disponible || 0) > 0 && (i?.stock_disponible || 0) < (i?.producto?.stock_minimo || 0))?.length;

      setAlertasStock({ sinStock, bajoMinimo: bajoMin });

      // Beneficio bruto (ventas base - coste estimado)
      // Usamos ventas base_imponible como proxy ya que no tenemos coste de ventas directo
      const beneficioBruto = totalBase; // Se mostrará como ventas s/IVA hasta tener coste de ventas
      const margenMedio = totalBase > 0 ? (beneficioBruto / totalBase) * 100 : 0;

      setKpis({
        ventasTotal: totalVentas,
        ventasBase: totalBase,
        numTickets,
        ticketMedio,
        comprasMes,
        pendientePago,
        facturasVencidas,
        stockVal,
        valorPvp,
        sinStock,
        bajoMin,
        margenMedio,
      });

      // Evolución diaria
      const byDay = {};
      (ventas || [])?.forEach(v => {
        const day = v?.fecha?.substring(0, 10);
        if (!day) return;
        if (!byDay?.[day]) byDay[day] = { day, fisico: 0, online: 0, total: 0 };
        if (v?.canal === 'TIENDA') byDay[day].fisico += v?.total || 0;
        else if (v?.canal === 'ONLINE') byDay[day].online += v?.total || 0;
        byDay[day].total += v?.total || 0;
      });
      setEvolucion(Object.values(byDay)?.sort((a, b) => a?.day?.localeCompare(b?.day))?.slice(-30));

      // Familias más vendidas (desde inventario como proxy)
      const { data: invFam } = await tiendaDb?.from('inventario_actual')?.select('stock_actual, producto:productos(pvp_venta, familia:familias_producto(nombre))');
      const byFam = {};
      (invFam || [])?.forEach(i => {
        const fam = i?.producto?.familia?.nombre || 'Sin familia';
        if (!byFam?.[fam]) byFam[fam] = { nombre: fam, valor: 0 };
        byFam[fam].valor += (i?.stock_actual || 0) * (i?.producto?.pvp_venta || 0);
      });
      setFamiliasData(Object.values(byFam)?.sort((a, b) => b?.valor - a?.valor)?.slice(0, 8));

      // Vencimientos próximos (7 días)
      const in7 = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];
      const venc = (compras || [])?.filter(c => c?.estado_pago !== 'PAGADA' && c?.fecha_vencimiento && c?.fecha_vencimiento >= today && c?.fecha_vencimiento <= in7);
      setVencimientos(venc?.slice(0, 5));

    } catch (e) {
      setError(e?.message);
    } finally {
      setLoading(false);
    }
  }, [dateFilter, channelFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  const dateOpts = DATE_FILTERS?.map(f => ({ value: f, label: f }));
  const channelOpts = CHANNEL_FILTERS?.map(f => ({ value: f, label: f }));

  const kpiCards = kpis ? [
    { label: 'Ventas (período)', value: fmtEur(kpis?.ventasTotal), icon: 'TrendingUp', color: ACCENT, sub: `${kpis?.numTickets} tickets` },
    { label: 'Ticket medio', value: fmtEur(kpis?.ticketMedio), icon: 'ShoppingBag', color: GOLD, sub: `${kpis?.numTickets} operaciones` },
    { label: 'Compras del mes', value: fmtEur(kpis?.comprasMes), icon: 'ShoppingCart', color: BLUE, sub: 'Facturas recibidas' },
    { label: 'Pendiente de pago', value: fmtEur(kpis?.pendientePago), icon: 'Clock', color: AMBER, sub: `${kpis?.facturasVencidas} vencidas` },
    { label: 'Stock valorizado', value: fmtEur(kpis?.stockVal), icon: 'Package', color: PURPLE, sub: 'A precio de coste' },
    { label: 'Valor potencial PVP', value: fmtEur(kpis?.valorPvp), icon: 'BarChart2', color: ACCENT, sub: 'Stock × PVP' },
    { label: 'Roturas de stock', value: String(kpis?.sinStock), icon: 'XCircle', color: RED, sub: 'Productos agotados' },
    { label: 'Bajo mínimo', value: String(kpis?.bajoMin), icon: 'AlertTriangle', color: AMBER, sub: 'Necesitan reposición' },
    { label: 'Facturas vencidas', value: String(kpis?.facturasVencidas), icon: 'AlertCircle', color: RED, sub: 'Pendientes de pago' },
  ] : [];

  return (
    <div className="min-h-screen px-4 py-6 md:px-8" style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <span className="text-2xl">🏪</span>
            <h1 className="font-oranienbaum font-bold text-white text-2xl md:text-3xl tracking-wide">Dashboard Tienda</h1>
          </div>
          <p className="font-oranienbaum text-white/40 text-sm ml-10">Vista ejecutiva · Datos reales de Supabase</p>
        </div>
        <button onClick={loadData} className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-oranienbaum transition-all" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
          <Icon name="RefreshCw" size={13} />Actualizar
        </button>
      </div>
      {/* Filtros */}
      <div className="flex flex-wrap gap-2 mb-6">
        <div className="w-36"><CorporateSelect value={dateFilter} onChange={setDateFilter} options={dateOpts} /></div>
        <div className="w-36"><CorporateSelect value={channelFilter} onChange={setChannelFilter} options={channelOpts} /></div>
      </div>
      {error && <div className="mb-4 p-4 rounded-xl flex items-center gap-3" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}><Icon name="AlertCircle" size={16} className="text-red-400 flex-shrink-0" /><p className="font-oranienbaum text-red-400 text-sm">{error}</p></div>}
      {/* KPI Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
        {loading ? (
          Array.from({ length: 9 })?.map((_, i) => (
            <div key={i} className="rounded-2xl p-4 animate-pulse" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', height: '100px' }} />
          ))
        ) : kpiCards?.map((k, i) => (
          <KPICard key={i} {...k} loading={loading} />
        ))}
      </div>
      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* Evolución diaria */}
        <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-oranienbaum font-semibold text-white text-sm">Evolución de Ventas</h3>
            <div className="flex items-center gap-3 text-[10px] font-oranienbaum text-white/40">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ background: ACCENT }} />Físico</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ background: BLUE }} />Online</span>
            </div>
          </div>
          {evolucion?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 gap-2">
              <Icon name="BarChart2" size={24} className="text-white/15" />
              <p className="font-oranienbaum text-white/25 text-xs">Sin ventas en el período seleccionado</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={evolucion} barSize={10}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="day" tick={{ fill: 'rgba(255,255,255,0.30)', fontSize: 9, fontFamily: 'Oranienbaum' }} axisLine={false} tickLine={false} tickFormatter={d => d?.slice(5)} />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.30)', fontSize: 9, fontFamily: 'Oranienbaum' }} axisLine={false} tickLine={false} tickFormatter={v => `${(v / 1000)?.toFixed(0)}k`} />
                <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 8, fontFamily: 'Oranienbaum', fontSize: 11 }} formatter={v => fmtEur(v)} />
                <Bar dataKey="fisico" fill={ACCENT} radius={[3, 3, 0, 0]} />
                <Bar dataKey="online" fill={BLUE} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Valor inventario por familia */}
        <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <h3 className="font-oranienbaum font-semibold text-white text-sm mb-4">Valor de Inventario por Familia</h3>
          {familiasData?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 gap-2">
              <Icon name="PieChart" size={24} className="text-white/15" />
              <p className="font-oranienbaum text-white/25 text-xs">Sin datos de inventario</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={familiasData} layout="vertical" barSize={10}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
                <XAxis type="number" tick={{ fill: 'rgba(255,255,255,0.30)', fontSize: 9, fontFamily: 'Oranienbaum' }} axisLine={false} tickLine={false} tickFormatter={v => `${(v / 1000)?.toFixed(0)}k`} />
                <YAxis type="category" dataKey="nombre" tick={{ fill: 'rgba(255,255,255,0.50)', fontSize: 9, fontFamily: 'Oranienbaum' }} axisLine={false} tickLine={false} width={80} />
                <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 8, fontFamily: 'Oranienbaum', fontSize: 11 }} formatter={v => fmtEur(v)} />
                <Bar dataKey="valor" fill={GOLD} radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
      {/* Operational Lists */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {/* Alertas stock */}
        <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${AMBER}15`, border: `1px solid ${AMBER}30` }}>
              <Icon name="AlertTriangle" size={14} style={{ color: AMBER }} />
            </div>
            <h4 className="font-oranienbaum font-semibold text-white/80 text-xs">Alertas de Stock</h4>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 rounded-lg" style={{ background: 'rgba(248,113,113,0.06)' }}>
              <span className="font-oranienbaum text-white/60 text-xs">Sin stock</span>
              <span className="font-oranienbaum font-bold text-sm" style={{ color: RED }}>{alertasStock?.sinStock}</span>
            </div>
            <div className="flex items-center justify-between p-2 rounded-lg" style={{ background: 'rgba(251,191,36,0.06)' }}>
              <span className="font-oranienbaum text-white/60 text-xs">Bajo mínimo</span>
              <span className="font-oranienbaum font-bold text-sm" style={{ color: AMBER }}>{alertasStock?.bajoMinimo}</span>
            </div>
          </div>
        </div>

        {/* Vencimientos próximos */}
        <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${RED}15`, border: `1px solid ${RED}30` }}>
              <Icon name="Calendar" size={14} style={{ color: RED }} />
            </div>
            <h4 className="font-oranienbaum font-semibold text-white/80 text-xs">Vencimientos 7 días</h4>
          </div>
          {vencimientos?.length === 0 ? (
            <p className="font-oranienbaum text-white/25 text-xs text-center py-4">Sin vencimientos próximos</p>
          ) : (
            <div className="space-y-1.5">
              {vencimientos?.map((v, i) => (
                <div key={i} className="flex items-center justify-between p-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.02)' }}>
                  <span className="font-oranienbaum text-white/60 text-xs truncate">{v?.fecha_vencimiento}</span>
                  <span className="font-oranienbaum text-xs font-bold" style={{ color: AMBER }}>{fmtEur(v?.pendiente_pago || v?.total)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Compras pendientes */}
        <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${BLUE}15`, border: `1px solid ${BLUE}30` }}>
              <Icon name="FileText" size={14} style={{ color: BLUE }} />
            </div>
            <h4 className="font-oranienbaum font-semibold text-white/80 text-xs">Facturas Pendientes</h4>
          </div>
          <div className="flex flex-col items-center justify-center py-4 gap-2">
            <p className="font-oranienbaum font-bold text-white text-2xl">{kpis?.facturasVencidas ?? '—'}</p>
            <p className="font-oranienbaum text-white/30 text-xs">vencidas</p>
            <p className="font-oranienbaum font-bold text-lg" style={{ color: AMBER }}>{fmtEur(kpis?.pendientePago)}</p>
            <p className="font-oranienbaum text-white/30 text-xs">pendiente total</p>
          </div>
        </div>

        {/* Resumen ventas */}
        <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}30` }}>
              <Icon name="TrendingUp" size={14} style={{ color: ACCENT }} />
            </div>
            <h4 className="font-oranienbaum font-semibold text-white/80 text-xs">Resumen Ventas</h4>
          </div>
          {kpis?.numTickets === 0 ? (
            <div className="flex flex-col items-center justify-center py-4 gap-2">
              <Icon name="ShoppingBag" size={20} className="text-white/15" />
              <p className="font-oranienbaum text-white/25 text-xs text-center">Sin ventas en el período</p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-oranienbaum text-white/50 text-xs">Total ventas</span>
                <span className="font-oranienbaum font-bold text-sm" style={{ color: ACCENT }}>{fmtEur(kpis?.ventasTotal)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-oranienbaum text-white/50 text-xs">Tickets</span>
                <span className="font-oranienbaum text-white/80 text-sm">{kpis?.numTickets}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-oranienbaum text-white/50 text-xs">Ticket medio</span>
                <span className="font-oranienbaum text-white/80 text-sm">{fmtEur(kpis?.ticketMedio)}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TiendaGeneral;
